import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useSsoCheck } from '@codespeak11/cognito-passwordless';
import { useAuth, ReauthDialog } from './auth';

// Layouts
import AppLayout from './components/layout/AppLayout';
import AuthLayout from './components/layout/AuthLayout';

// Pages
import Dashboard from './pages/Dashboard';
import PersonaGenerator from './pages/PersonaGenerator';
import PersonaDetail from './pages/PersonaDetail';
import PersonaList from './pages/PersonaList';
import PersonaProjects from './pages/PersonaProjects';
import ProjectDetail from './pages/ProjectDetail';
import PromptTemplates from './pages/PromptTemplates';
import Settings from './pages/Settings';
import BillingSummary from './pages/BillingSummary';
import Login from './pages/Login';
import Register from './pages/Register';
import SsoCallbackPage from './pages/SsoCallbackPage';

// Protected Route Wrapper
//
// Per bdanceinfra/docs/spa-auth-integration.md Step 7, useSsoCheck() should
// run on returning visitors so a still-valid session on another BrainDance
// site silently signs them in here. We mount it here so unauthenticated
// users get a hidden prompt=none redirect attempt BEFORE being bounced to
// /login — turning a "your session expired" experience into a no-op for
// users with a healthy shared session cookie.
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const { status } = useSsoCheck({ skip: isAuthenticated });

  if (isLoading || status === 'checking') {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Public Route Wrapper (redirects to app if already logged in)
//
// We intentionally do NOT run useSsoCheck() here. When a session truly
// expires (refresh token revoked / cross-site cookie cleared), the user
// lands on /login from the SsoCallback `onLoginRequired` path. Running
// another silent prompt=none check from /login produces a spinner-then-
// button flicker for users with no central session, and in pathological
// cases (callback errors + StrictMode double-mount) can interfere with
// the user's ability to click "Sign In with BrainDance". The /login page
// is the explicit "you are signed out" surface — keep it side-effect free.
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <>
      <ReauthDialog />
      <Routes>
        {/* SSO callback — outside layout to avoid rendering app chrome during redirect */}
        <Route path="/auth/callback" element={<SsoCallbackPage />} />

      {/* Auth Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <AuthLayout>
              <Login />
            </AuthLayout>
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <AuthLayout>
              <Register />
            </AuthLayout>
          </PublicRoute>
        }
      />

      {/* Protected App Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="personas">
          <Route index element={<PersonaList />} />
          <Route path="new" element={<PersonaGenerator />} />
          <Route path=":id" element={<PersonaDetail />} />
        </Route>
        <Route path="projects">
          <Route index element={<PersonaProjects />} />
          <Route path=":id" element={<ProjectDetail />} />
        </Route>
        <Route path="templates" element={<PromptTemplates />} />
        <Route path="billing-summary" element={<BillingSummary />} />
        <Route path="settings" element={<Settings />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

export default App;
